import React, { useEffect, useState, useCallback } from "react";
import {
  Box,
  Container,
  Card,
  Skeleton,
  Typography,
  Stack,
} from "@mui/material";

import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import AccountBalanceWalletIcon from "@mui/icons-material/AccountBalanceWallet";
import RefreshIcon from "@mui/icons-material/Refresh";
import { useParams, useHistory } from "react-router-dom";
import ThreeSlotMachineDetailCard from "./components/ThreeSlotMachineDetailCard";
import NavBar from "../../../Components/NavBar";
import { connectWallet as triggerConnectWallet } from "../../../../static/walletUtils";
import { Path } from "../../../../static/Constants";
import { colors, borderRadiusPx } from "../../../../theme";
import { CommonBreadcrumbs, CommonButton, toast } from "../../../Components/ui";
import FailedPopup from "../../../Components/FailedPopup";
import { THREE_SLOT_DETAILS_THEME } from "./components/threeSlotConfig";
import slotInstance, { ca as slotContractAddress } from "../../../Service/slotInstance";
import tokenInstance from "../../../Service/tokencinstance";
import web3 from "../../../Service/web3";

const ThreeSlotLotteryDetails = () => {
  const { id } = useParams();
  const history = useHistory();

  const [account, setAccount] = useState("");
  const [isCheckingWallet, setIsCheckingWallet] = useState(true);

  const [spinLoading, setSpinLoading] = useState(false);
  const [lastSpinResult, setLastSpinResult] = useState(null);

  // Failed / Error popup state (replaces toasts)
  const [failedModal, setFailedModal] = useState({
    open: false,
    title: "Transaction Failed",
    message: "Oops, something went wrong!",
    description: "Please verify your wallet balance and try again.",
    onRetry: null,
  });

  // User-specific BET token balance from wallet
  const [balance, setBalance] = useState("0.00");
  const [win, setWin] = useState("0.00");

  const [lotteryData, setLotteryData] = useState(null);
  const [loading, setLoading] = useState(true);

  // Fetch the slot game from contract matching /all-three-slotlottery mapping
    // Fetch the slot game from contract matching /all-three-slotlottery mapping
    const fetchSlotGame = useCallback(async (isSilentArg = false) => {
        const isSilent = isSilentArg === true;
        if (!id) return;
        if (!isSilent) {
            setLoading(true);
        }

        try {
            if (!slotInstance?.methods) {
                console.warn("slotInstance methods not available");
                if (!isSilent) {
                    setLoading(false);
                }
                return;
            }

            let rawGame = null;

            // 1. First attempt: call slotGames(id) directly
            if (slotInstance.methods.slotGames) {
                try {
                    const game = await slotInstance.methods.slotGames(id).call();
                    const gId = game?.gameId ?? game?.[0];
                    const owner = game?.owner ?? game?.[12]; // Updated index for owner
                    if (
                        gId &&
                        gId.toString() === id.toString() &&
                        Number(gId) > 0 &&
                        owner &&
                        owner !== "0x0000000000000000000000000000000000000000"
                    ) {
                        rawGame = game;
                    }
                } catch (e) {
                    console.warn(`slotGames(${id}) call failed:`, e);
                }
            }

            // 2. Fallback: search within getOngoingGames()
            if (!rawGame && slotInstance.methods.getOngoingGames) {
                try {
                    const ongoingGames = await slotInstance.methods.getOngoingGames().call();
                    const found = ongoingGames.find(
                        (g) => (g.gameId || g[0])?.toString() === id.toString()
                    );
                    if (found) {
                        rawGame = found;
                    }
                } catch (e) {
                    console.warn("getOngoingGames() fallback failed:", e);
                }
            }

            if (rawGame) {
                console.log("Fetched 3-Slot game data:", rawGame);

                // BULLETPROOF MAPPING (Matches your exact Solidity struct)
                const gameId = rawGame.gameId || rawGame[0] || id;
                const reels = rawGame.reels || rawGame[1] || 3;
                const isCrypto = rawGame.isCrypto ?? rawGame[2];
                const minBet = rawGame.minBet || rawGame[3] || "0";
                const maxBet = rawGame.maxBet || rawGame[4] || "0";
                const progBps = Number(rawGame.progressiveContributionBps || rawGame[5]) || 0;
                const miniBps = Number(rawGame.miniBps || rawGame[6]) || 0;
                const majorBps = Number(rawGame.majorBps || rawGame[7]) || 0;
                const megaBps = Number(rawGame.megaBps || rawGame[8]) || 0;
                const active = rawGame.active ?? rawGame[9];
                const startTime = rawGame.startTime || rawGame[10] || "0";
                const endTime = rawGame.endTime || rawGame[11] || "0";
                const owner = rawGame.owner || rawGame[12];

                // indices 13, 14, 15 are partner and admin details
                const miniPool = rawGame.miniPool || rawGame[16] || "0";
                const majorPool = rawGame.majorPool || rawGame[17] || "0";
                const megaPool = rawGame.megaPool || rawGame[18] || "0";

                // Format Wei to Ether safely
                const ticketPriceFormatted = web3?.utils?.fromWei
                    ? web3.utils.fromWei(String(minBet), "ether")
                    : String(minBet);

                const maxBetFormatted = web3?.utils?.fromWei
                    ? web3.utils.fromWei(String(maxBet), "ether")
                    : String(maxBet);

                const miniPrizeFormatted = web3?.utils?.fromWei
                    ? web3.utils.fromWei(String(miniPool), "ether")
                    : String(miniPool);

                const majorPrizeFormatted = web3?.utils?.fromWei
                    ? web3.utils.fromWei(String(majorPool), "ether")
                    : String(majorPool);

                const megaPrizeFormatted = web3?.utils?.fromWei
                    ? web3.utils.fromWei(String(megaPool), "ether")
                    : String(megaPool);

                const numericMini = Number(miniPrizeFormatted) || 0;
                const numericMajor = Number(majorPrizeFormatted) || 0;
                const numericMega = Number(megaPrizeFormatted) || 0;
                const numericTicketPrice = Number(ticketPriceFormatted) || 0;
                const numericMaxBet = Number(maxBetFormatted) || 0;

                const totalProgContribution = (numericTicketPrice * progBps) / 10000;

                const mapped = {
                    lotteryId: Number(gameId),
                    gameId: Number(gameId),
                    name: `3-SlotBET #${gameId}`,
                    creator: owner
                        ? `${owner.slice(0, 6)}...${owner.slice(-4)}`
                        : "AutoBET Official",
                    owner,
                    reels: Number(reels),
                    active,
                    startTime,
                    endTime,
                    ticketPrice: ticketPriceFormatted,
                    minBet: ticketPriceFormatted || "1.0",
                    maxBet: maxBetFormatted || "100.0",
                    slotCategory: isCrypto ? "crypto" : "fiat",
                    currency: "BET",
                    jackpots: {
                        mini: {
                            amount: numericMini.toFixed(2),
                            seedValue: ((numericMaxBet * miniBps) / 10000).toFixed(2),
                            contributionPerSpin: ((totalProgContribution * miniBps) / 10000).toFixed(4),
                        },
                        major: {
                            amount: numericMajor.toFixed(2),
                            seedValue: ((numericMaxBet * majorBps) / 10000).toFixed(2),
                            contributionPerSpin: ((totalProgContribution * majorBps) / 10000).toFixed(4),
                        },
                        mega: {
                            amount: numericMega.toFixed(2),
                            seedValue: ((numericMaxBet * megaBps) / 10000).toFixed(2),
                            contributionPerSpin: ((totalProgContribution * megaBps) / 10000).toFixed(4),
                        },
                    },
                };

                setLotteryData(mapped);
            } else {
                if (!isSilent) {
                    toast.error(`3-Slot game #${id} not found.`);
                }
                setLotteryData(null);
            }
        } catch (error) {
            console.error("fetchSlotGame error:", error);
            if (!isSilent) {
                toast.error("Unable to load 3-SlotBET game.");
            }
            setLotteryData(null);
        } finally {
            if (!isSilent) {
                setLoading(false);
            }
        }
    }, [id]);

  useEffect(() => {
    fetchSlotGame(false);
  }, [fetchSlotGame]);

  // Fetch token balance for connected account
  const fetchBalance = useCallback(async () => {
    if (!account || !tokenInstance?.methods?.balanceOf) {
      setBalance("0.00");
      return;
    }
    try {
      const balWei = await tokenInstance.methods.balanceOf(account).call();
      const balEth = web3.utils.fromWei(String(balWei), "ether");
      setBalance(Number(balEth).toFixed(2));
    } catch (err) {
      console.warn("Could not fetch user token balance:", err);
      setBalance("0.00");
    }
  }, [account]);

  useEffect(() => {
    fetchBalance();
  }, [fetchBalance]);

  // Check whether a wallet is already connected
  useEffect(() => {
    const checkWalletConnection = async () => {
      if (!window.ethereum) {
        setIsCheckingWallet(false);
        return;
      }

      try {
        const accounts = await window.ethereum.request({
          method: "eth_accounts",
        });

        if (accounts && accounts.length > 0) {
          setAccount(accounts[0]);
        }
      } catch (error) {
        console.error("Failed to check wallet connection:", error);
      } finally {
        setIsCheckingWallet(false);
      }
    };

    checkWalletConnection();
  }, []);

  // Listen for wallet changes
  useEffect(() => {
    if (!window.ethereum) return;

    const handleAccountsChanged = (accounts) => {
      setAccount(accounts?.[0] || "");
    };

    window.ethereum.on("accountsChanged", handleAccountsChanged);

    return () => {
      window.ethereum.removeListener("accountsChanged", handleAccountsChanged);
    };
  }, []);

  const handleConnectWallet = async () => {
    const res = await triggerConnectWallet();
    if (res.account) {
      setAccount(res.account);
    }
  };

  if (isCheckingWallet) {
    return null;
  }

    const waitForSpinResult = async (requestId) => {
        for (let i = 0; i < 20; i++) {
            const result = await slotInstance.methods.getSpinResult(requestId).call();

            if (result?.fulfilled) {
                const reels = await slotInstance.methods.getReels3(requestId).call();

                return {
                    requestId,
                    result,
                    reels: [
                        Number(reels.s0 ?? reels[0]),
                        Number(reels.s1 ?? reels[1]),
                        Number(reels.s2 ?? reels[2]),
                    ],
                    payout: web3.utils.fromWei(String(result.payout || "0"), "ether"),
                };
            }

            await new Promise((resolve) => setTimeout(resolve, 3000));
        }

        throw new Error("Spin result is still pending. Please check again shortly.");
    };

    const handleSpin3Reel = async (lotteryData, betAmount, onTxConfirmed) => {
        try {
            if (!window.ethereum) {
                setFailedModal({
                    open: true,
                    title: "Wallet Not Found",
                    message: "Web3 wallet not detected in your browser.",
                    description: "Please install or unlock MetaMask or another Web3-compatible wallet to play.",
                });
                return null;
            }

            if (!slotInstance?.methods?.spin3Reel) {
                setFailedModal({
                    open: true,
                    title: "Contract Error",
                    message: "spin3Reel method not found on slot contract.",
                    description: "Please verify that the correct slot machine contract is deployed.",
                });
                return null;
            }

            setSpinLoading(true);

            const accounts = await web3.eth.getAccounts();

            if (!accounts || accounts.length === 0) {
                setFailedModal({
                    open: true,
                    title: "Wallet Required",
                    message: "Please connect your wallet first.",
                    description: "Your Web3 wallet needs to be connected to sign the spin transaction.",
                    onRetry: handleConnectWallet,
                });
                return null;
            }

            const activeAccount = accounts[0];
            const gameId = lotteryData?.gameId || lotteryData?.lotteryId;

            if (!gameId) {
                setFailedModal({
                    open: true,
                    title: "Invalid Game",
                    message: "Slot game information is missing.",
                    description: "Please refresh the page and try again.",
                });
                return null;
            }

            if (!betAmount || Number(betAmount) <= 0) {
                setFailedModal({
                    open: true,
                    title: "Invalid Bet",
                    message: "Invalid bet amount.",
                    description: "Please ensure your bet amount is greater than zero.",
                });
                return null;
            }

            const betAmountWei = web3.utils.toWei(String(betAmount), "ether");

            const minBetWei = lotteryData?.minBet
                ? web3.utils.toWei(String(lotteryData.minBet), "ether")
                : "0";

            const maxBetWei = lotteryData?.maxBet
                ? web3.utils.toWei(String(lotteryData.maxBet), "ether")
                : "0";

            if (web3.utils.toBN(betAmountWei).lt(web3.utils.toBN(minBetWei))) {
                setFailedModal({
                    open: true,
                    title: "Minimum Bet Required",
                    message: `Minimum bet is ${lotteryData.minBet} BET.`,
                    description: `Your current bet is below the machine minimum of ${lotteryData.minBet} BET.`,
                });
                return null;
            }

            if (
                maxBetWei !== "0" &&
                web3.utils.toBN(betAmountWei).gt(web3.utils.toBN(maxBetWei))
            ) {
                setFailedModal({
                    open: true,
                    title: "Maximum Bet Exceeded",
                    message: `Maximum bet is ${lotteryData.maxBet} BET.`,
                    description: `Your current bet exceeds the machine limit of ${lotteryData.maxBet} BET.`,
                });
                return null;
            }
            toast.info("Checking BET token allowance...");

            const currentAllowance = await tokenInstance.methods
                .allowance(activeAccount, slotContractAddress)
                .call();

            if (web3.utils.toBN(currentAllowance).lt(web3.utils.toBN(betAmountWei))) {
                toast.info("Approving BET tokens...");
                await tokenInstance.methods
                    .approve(slotContractAddress, betAmountWei)
                    .send({ from: activeAccount });

                // ⏳ 1st DELAY: Give the RPC node time to breathe after the approval
                await new Promise(resolve => setTimeout(resolve, 2000));
            }

            // ⏳ 2nd DELAY: Prevent "429 Rate Limit" before the Spin transaction
            await new Promise(resolve => setTimeout(resolve, 1500));

            toast.info("Sending spin transaction...");

            let receiptConfirmed = false;
            const confirmReceipt = (rcpt) => {
                if (!receiptConfirmed) {
                    receiptConfirmed = true;
                    if (onTxConfirmed && typeof onTxConfirmed === "function") {
                        try {
                            onTxConfirmed(rcpt);
                        } catch (e) {
                            console.error("onTxConfirmed callback error:", e);
                        }
                    }
                }
            };

            // Force Web3 to not double-estimate gas by providing a hard limit
            const sendPromise = slotInstance.methods
                .spin3Reel(gameId, betAmountWei) // NOTE: Use spin5Reel for the 5-Slot page!
                .send({
                    from: activeAccount,
                    gas: 1000000,
                });

            if (sendPromise && typeof sendPromise.on === "function") {
                sendPromise.on("receipt", (rcpt) => {
                    confirmReceipt(rcpt);
                });
            }

            const receipt = await sendPromise;
            confirmReceipt(receipt);

            console.log("spin3Reel receipt:", receipt);

            const requestId =
                receipt?.events?.SlotSpinRequested?.returnValues?.requestId ||
                receipt?.events?.SlotSpinRequested?.returnValues?.[2];

            if (!requestId) {
                return {
                    receipt,
                    requestId: null,
                    pending: true,
                };
            }

            toast.info("Spin requested. Waiting for result...");

            const spinResult = await waitForSpinResult(requestId);

            setLastSpinResult(spinResult);

            // Silently refresh game state, jackpots, and user balance without unmounting
            fetchSlotGame(true);
            fetchBalance();

            return spinResult;
        } catch (error) {
            console.error("spin3Reel error:", error);

            const isUserRejected =
                error?.code === 4001 ||
                error?.message?.toLowerCase().includes("user rejected") ||
                error?.message?.toLowerCase().includes("user denied");

            if (isUserRejected) {
                setFailedModal({
                    open: true,
                    title: "Transaction Rejected",
                    message: "You cancelled or rejected the transaction in MetaMask.",
                    description: "No BET tokens were spent. You can try again whenever you are ready.",
                    onRetry: () => handleSpin3Reel(lotteryData, betAmount, onTxConfirmed),
                });
            } else {
                setFailedModal({
                    open: true,
                    title: "Transaction Failed",
                    message:
                        error?.message && error.message.length < 140
                            ? error.message
                            : "The on-chain spin transaction could not be executed.",
                    description: "Please check your network connection, token balance, and try again.",
                    onRetry: () => handleSpin3Reel(lotteryData, betAmount, onTxConfirmed),
                });
            }

            return null;
        } finally {
            setSpinLoading(false);
        }
    };

  return (
    <Box
      sx={{
        position: "relative",
        overflowX: "hidden",
        minHeight: "100vh",
        bgcolor: THREE_SLOT_DETAILS_THEME.pageBg || colors.dashboard.canvas,
        display: "flex",
        flexDirection: "column",
      }}
    >
      <NavBar />
      <Container
        maxWidth={THREE_SLOT_DETAILS_THEME.containerMaxWidth || "lg"}
        className="slot-page-container"
        sx={{
          pt: { xs: "90px", sm: "100px", md: "108px" },
          pb: { xs: 4, md: 8 },
          flexGrow: 1,
          display: "flex",
          flexDirection: "column",
          justifyContent: "flex-start",
          alignItems: "center",
        }}
      >
        <Box
          sx={{
            width: "100%",
            maxWidth: THREE_SLOT_DETAILS_THEME.cardMaxWidth || 1040,
            mb: 2.5,
          }}
        >
          {/* Breadcrumbs Navigation */}
          <CommonBreadcrumbs
            items={[
              { label: "Home", path: Path.root },
              { label: "3-SlotBET", path: Path.ThreeSlotlottery },
              { label: lotteryData?.name || `3-SlotBET #${id}` },
            ]}
          />

          {/* Action Row */}
          <Box
            sx={{
              mt: 1.5,
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
            }}
          >
            <CommonButton
              variant="refresh"
              size="small"
              startIcon={<ArrowBackIcon />}
              onClick={() => history.push(Path.ThreeSlotlottery)}
            >
              Back to Arena
            </CommonButton>

            {!account && (
              <CommonButton
                variant="primary"
                size="small"
                startIcon={<AccountBalanceWalletIcon />}
                onClick={handleConnectWallet}
              >
                Connect Wallet
              </CommonButton>
            )}
          </Box>
        </Box>

        {/* The 3-Slot Interface */}
        <Box sx={{ width: "100%", maxWidth: THREE_SLOT_DETAILS_THEME.cardMaxWidth || 1040 }}>
          {loading ? (
            <Card
              sx={{
                p: { xs: 3, sm: 5 },
                borderRadius: borderRadiusPx.xl,
                bgcolor: "#ffffff",
                border: "1px solid #e2e8f0",
                boxShadow: "0 4px 20px rgba(0, 0, 0, 0.04)",
              }}
            >
              <Skeleton variant="rectangular" height={4} sx={{ mb: 3 }} />
              <Skeleton variant="rounded" height={60} sx={{ mb: 3, borderRadius: "12px" }} />
              <Skeleton variant="rounded" height={80} sx={{ mb: 3, borderRadius: "12px" }} />
              <Skeleton variant="rounded" height={260} sx={{ mb: 3, borderRadius: "16px" }} />
              <Skeleton variant="rounded" height={54} sx={{ borderRadius: "12px" }} />
            </Card>
          ) : !lotteryData ? (
            <Card
              sx={{
                p: { xs: 4, sm: 6 },
                textAlign: "center",
                borderRadius: borderRadiusPx.xl,
                backgroundColor: "#ffffff",
                border: "1px solid #e2e8f0",
                boxShadow: "0 2px 8px rgba(0, 0, 0, 0.04)",
                my: 3,
              }}
            >
              <Box
                sx={{
                  width: 68,
                  height: 68,
                  mx: "auto",
                  mb: 2,
                  borderRadius: "50%",
                  bgcolor: "#faf5ff",
                  color: "#8b5cf6",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  fontSize: "2rem",
                  border: "1.5px solid #e9d5ff",
                  boxShadow: "0 2px 10px rgba(139, 92, 246, 0.15)",
                }}
              >
                🎰
              </Box>
              <Typography
                variant="h5"
                sx={{ fontWeight: 800, color: colors.slate[900], mb: 1 }}
              >
                {`3-Slot Game #${id} Not Found`}
              </Typography>
              <Typography
                variant="body2"
                sx={{
                  color: colors.slate[600],
                  mb: 3,
                  maxWidth: 460,
                  mx: "auto",
                  lineHeight: 1.6,
                }}
              >
                This slot machine could not be found or is not currently active on-chain.
              </Typography>
              <Stack direction="row" spacing={2} justifyContent="center" flexWrap="wrap">
                <CommonButton
                  variant="primary"
                  onClick={() => history.push(Path.ThreeSlotlottery)}
                  sx={{
                    bgcolor: "#8b5cf6",
                    "&:hover": { bgcolor: "#7c3aed" },
                  }}
                >
                  Back to 3-SlotBET Arena
                </CommonButton>
                <CommonButton
                  variant="refresh"
                  startIcon={<RefreshIcon />}
                  onClick={() => fetchSlotGame(false)}
                >
                  Retry
                </CommonButton>
              </Stack>
            </Card>
          ) : (
            <ThreeSlotMachineDetailCard
              lotteryData={lotteryData}
              account={account}
              onSpin={handleSpin3Reel}
              spinLoading={spinLoading}
              lastSpinResult={lastSpinResult}
              balance={balance}
              win={win}
              setBalance={setBalance}
              setWin={setWin}
            />
          )}
        </Box>
      </Container>

      {/* Transaction & Action Error Popup */}
      <FailedPopup
        open={failedModal.open}
        show={failedModal.open}
        setShowModal={(open) => setFailedModal((prev) => ({ ...prev, open }))}
        onClose={() => setFailedModal((prev) => ({ ...prev, open: false }))}
        title={failedModal.title}
        message={failedModal.message}
        description={failedModal.description}
        onRetry={failedModal.onRetry}
        btnName="Close"
      />
    </Box>
  );
};

export default ThreeSlotLotteryDetails;